import { Icon } from "@iconify/react"
import { useQuery, keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Drawer, Flex, Form, Grid, Input, InputNumber, message, Pagination, Radio, Select, Table, Tag, Typography } from "antd"
import TableAction from "../../../components/TableAction";
import { useEffect, useState } from "react";
import useTitleStore from "../../../store/titleStore";
import useDebounce from "../../../hooks/useDebounce";
import { useTableHeight } from "../../../hooks/useTableHeight";
import { masterDataItemService } from "../../../services/master-data-item.service";
import { masterDataTypeService } from "../../../services/master-data-type.service";
import RenderIf from "../../../components/RenderIf";

export default function MasterDataItemIndex(){

  const breakpoint = Grid.useBreakpoint()

  const [form] = Form.useForm()
  const typeId = Form.useWatch('typeId', form)

  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();

  const tableHeight = useTableHeight();

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const [type, setType] = useState()
  const [selectedMasterDataType, setSelectedMasterDataType] = useState()
  const debouncedKeyword = useDebounce(keyword, 500);
  const [drawerOpt, setDrawerOpt] = useState({
    open: false,
    id: null
  })
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: [
      {
        field: 'name',
        operator: 'like',
        value: debouncedKeyword
      },
      {
        field: 'typeId',
        operator: '=',
        value: type
      },
    ]
  })

  useEffect(() => {
    setTitle("Options");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      filter: [
        {
          field: 'name',
          operator: 'like',
          value: debouncedKeyword
        },
        {
          field: 'typeId',
          operator: '=',
          value: type
        },
      ]
    }));
  }, [debouncedKeyword, type]);

  const { data: masterDataItem , isLoading, isRefetching } = useQuery({
    queryKey: ['masterDataItem', dataParams],
    queryFn: () => masterDataItemService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const { data: masterDataType } = useQuery({
    queryKey: ['masterDataType'],
    queryFn: () => masterDataTypeService.fetchAllWithoutPagination(),
  })

  useEffect(() => {
    const selected = masterDataType?.data.find(e => e.id == typeId)
    setSelectedMasterDataType(selected)
    if(!drawerOpt.id){
      form.setFieldValue('attributes',selected?.attributes?.map(e => ({ id: e.id, name: e.name, type: e.dataType, value: '' })))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId])

  const storeMutation = useMutation({
    mutationFn: val => masterDataItemService.store(val),
    onSuccess: () => {
      queryClient.invalidateQueries('masterDataItem');
      messageApi.success('Data berhasil ditambah');
      handleCloseDrawerOpt()
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan');
    }
  })

  const updateMutation = useMutation({
    mutationFn: val => masterDataItemService.update(val),
    onSuccess: () => {
      queryClient.invalidateQueries('masterDataItem');
      messageApi.success('Data berhasil diubah');
      handleCloseDrawerOpt()
    },
    onError: (error) => {
      console.log(error)
      messageApi.error('Terjadi kesalahan');
    }
  })

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => masterDataItemService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      messageApi.success('Data berhasil dihapus');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const handleEdit = record => {

    form.setFieldsValue(record)
    form.setFieldsValue({
      id: record.id,
      name: record.name,
      value: record.value,
      status: record.status
    });
    if(record?.attributes?.length > 0){
      console.log("Record attributes:", record.attributes);
    
      // Penting: Pastikan struktur data sesuai dengan ekspektasi Form.List
      const formattedAttributes = record.attributes.map(attr => ({
        id: attr.id,
        name: attr.name,
        type: attr.dataType,
        value: attr.value
      }));
      
      console.log("Formatted attributes:", formattedAttributes);
      
      // Set nilai attributes
      form.setFieldValue('attributes', formattedAttributes);
    }
    setDrawerOpt({ open: true, id: record.id })
  }

  const handleCloseDrawerOpt = () => {
    form.resetFields()
    setDrawerOpt({ open: false, id: null })
  }

  const handleSubmit = val => {
    if(drawerOpt.id) updateMutation.mutate({id: drawerOpt.id, ...val})
    else storeMutation.mutate(val)
    // console.log(val)
  }

  const masterDataItems = masterDataItem?.data;
  const pagination = masterDataItem?.pagination || { total: 0, page: 1 };

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      fixed: breakpoint.md && 'left',
      render: (text, record, index) =>  index + 1,
    },
    {
      title: 'Jenis',
      dataIndex: 'type',
      width: 180,
      render: val => val?.name
    },
    {
      title: 'Label',
      dataIndex: 'name',
      width: 180,
    },
    {
      title: 'Nilai',
      dataIndex: 'value',
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: val => <Tag color={val ? 'green' : 'red'} >{val ? 'Active' : 'Inactive'}</Tag>
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <TableAction
          onClickEdit={() => handleEdit(record)}
          handleDelete={() => handleDelete(record.id)}
          deleteLoading={deleteLoading}
        />
      )
    },
  ]

  return (
    <>
      {contextHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <Flex gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
          <Input 
            style={{ width: 320 }} 
            prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
            placeholder="cari"
            onChange={e => setKeyword(e.target.value)}
            value={keyword}
            allowClear
          />
          <Select
            options={masterDataType?.data?.map(e => ({ label: e.name, value: e.id }))}
            placeholder="cari berdasarkan kategori"
            allowClear
            style={{ width: 320, marginRight: 'auto' }}
            value={type}
            onChange={(e) => setType(e)}
          />
          <Button 
            variant="solid" 
            color="primary"  
            icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
            onClick={() => setDrawerOpt({ open: true, id: null })}
            style={{ minWidth: 38 }}
          >
            Tambah Master Data
          </Button>
        </Flex>
        <Table
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={masterDataItems}
          pagination={false}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={pagination.total}
          showSizeChanger={{
            showSearch: false
          }}
          pageSizeOptions={['10', '20', '50']}
          current={pagination.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <Drawer
        open={drawerOpt.open}
        title={drawerOpt.id ? "Ubah Data" : "Tambah Data"}
        onClose={handleCloseDrawerOpt}
        footer={
          <Flex gap={16} >
            <Button 
              block
              disabled={storeMutation.isPending} 
              loading={storeMutation.isPending} 
              variant="outlined" 
              color="primary" 
            >
              Batal
            </Button>
            <Button 
              block
              variant="solid" 
              color="primary" 
              onClick={() => form.submit()}
              disabled={storeMutation.isPending} 
              loading={storeMutation.isPending} 
            >
              Simpan
            </Button>
          </Flex>
        }
      > 
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
        >
          <Form.Item  rules={[{ required: true }]} name={'typeId'} label="Jenis" >
            <Select
              options={masterDataType?.data?.map(e => ({ label: e.name, value: e.id }))}
            />
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name={'name'} label="Label" >
            <Input/>
          </Form.Item>
          <Form.Item rules={[{ required: true }]} name={'value'} label="Nilai" >
            <Input/>
          </Form.Item>
          <Form.Item  rules={[{ required: true }]} name={'status'} label="Status" >
            <Radio.Group>
              <Radio style={{ marginRight: 80}} value={true} >Active</Radio>
              <Radio value={false} >Inactive</Radio>
            </Radio.Group>
          </Form.Item>
          <RenderIf when={selectedMasterDataType?.attributes?.length != 0} >
            <Typography.Text strong style={{ display: 'inline-block', marginBottom: 8 }} >
              Attribute
            </Typography.Text>
            <Form.List
              name={'attributes'}
            >
              {(fields) => (
                <>
                  {fields.map(field => {

                    const attributeType = form.getFieldValue(['attributes', field.name, 'type']) || selectedMasterDataType?.attributes[field.name]?.dataType;
                    
                    return (
                      <Form.Item
                        key={field.key}
                        label={selectedMasterDataType?.attributes[field.name]?.name}
                      >
                        {attributeType === 'Option' ? (
                          <Form.Item name={[field.name, 'value']} noStyle>
                            <Select
                              options={selectedMasterDataType?.attributes[field.name]?.options?.map(e => ({ 
                                label: e.name, 
                                value: e.id 
                              })) || []}
                            />
                          </Form.Item>
                        ) : attributeType === 'Numeric' ? (
                          <Form.Item name={[field.name, 'value']} noStyle>
                            <InputNumber style={{ width: '100%' }} />
                          </Form.Item>
                        ) : (
                          <Form.Item name={[field.name, 'value']} noStyle>
                            <Input />
                          </Form.Item>
                        )}
                      </Form.Item>
                    );
                  })}
                </>
              )}
            </Form.List>
          </RenderIf>
        </Form>
      </Drawer>
    </>
  )
}